const RECOVERY_FLAG = 'runtime_recovery_attempted_at';
const RECOVERY_COOLDOWN_MS = 2 * 60 * 1000;
const RECOVERY_QUERY_PARAMS = ['_recover', '_trigger', '_asset'] as const;
let runtimeRecoveryListenersInstalled = false;

const RUNTIME_FAILURE_RE = /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Importing a module script failed|not a valid JavaScript MIME type|Expected a JavaScript module script|disallowed MIME type|Unexpected token '<'.*not valid JSON|Received a promise that resolves to: undefined|Lazy element type must resolve to a class or function|Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/i;
const ASSET_CONTEXT_RE = /\/assets\/|Google3DGlobeView|main-[A-Za-z0-9_-]+\.js|vendor-[A-Za-z0-9_-]+\.js/i;
const ASSET_URL_RE = /\/assets\/[^?#]+\.(?:js|mjs|css)(?:[?#]|$)/i;

export function isRecoverableRuntimeFailure(message?: string, stack?: string): boolean {
  const msg = message || '';
  const trace = stack || '';
  if (!RUNTIME_FAILURE_RE.test(msg)) return false;
  if (/Unexpected token '<'/i.test(msg)) {
    return ASSET_CONTEXT_RE.test(msg) || ASSET_CONTEXT_RE.test(trace);
  }
  return true;
}

export async function attemptRuntimeRecovery(trigger: string): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    const now = Date.now();
    const lastAttempt = Number(sessionStorage.getItem(RECOVERY_FLAG) || '0');
    if (lastAttempt && now - lastAttempt < RECOVERY_COOLDOWN_MS) {
      return false;
    }

    sessionStorage.setItem(RECOVERY_FLAG, String(now));

    // Best-effort cleanup of stale caches / registrations before reload.
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
    } catch { /* noop */ }

    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
    } catch { /* noop */ }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('_recover', String(now));
    nextUrl.searchParams.set('_trigger', trigger.slice(0, 24));
    window.location.replace(nextUrl.toString());
    return true;
  } catch {
    return false;
  }
}

export function clearRuntimeRecoveryNavigationState(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const nextUrl = new URL(window.location.href);
    let changed = false;
    for (const param of RECOVERY_QUERY_PARAMS) {
      if (nextUrl.searchParams.has(param)) {
        nextUrl.searchParams.delete(param);
        changed = true;
      }
    }
    if (!changed) return false;
    window.history.replaceState(window.history.state, document.title, nextUrl.toString());
    return true;
  } catch {
    return false;
  }
}

function getEventMessage(event: Event): string {
  const errorEvent = event as ErrorEvent;
  if (errorEvent.message) return errorEvent.message;
  const target = event.target as { src?: string; href?: string } | null;
  return target?.src || target?.href || '';
}

function getRejectionMessage(reason: unknown): { message: string; stack?: string } {
  if (typeof reason === 'string') return { message: reason };
  if (reason && typeof reason === 'object') {
    const candidate = reason as { message?: unknown; stack?: unknown };
    return {
      message: typeof candidate.message === 'string' ? candidate.message : String(reason),
      stack: typeof candidate.stack === 'string' ? candidate.stack : undefined,
    };
  }
  return { message: String(reason || '') };
}

function isAssetLoadEvent(event: Event): boolean {
  const target = event.target as { src?: string; href?: string } | null;
  const url = target?.src || target?.href || '';
  return ASSET_URL_RE.test(url);
}

export function installRuntimeRecoveryListeners(): void {
  if (runtimeRecoveryListenersInstalled || typeof window === 'undefined') return;
  runtimeRecoveryListenersInstalled = true;
  clearRuntimeRecoveryNavigationState();

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    void attemptRuntimeRecovery('vite-preload');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const { message, stack } = getRejectionMessage(event.reason);
    if (!isRecoverableRuntimeFailure(message, stack)) return;
    event.preventDefault();
    void attemptRuntimeRecovery('unhandled-rejection');
  }, true);

  window.addEventListener('error', (event) => {
    const message = getEventMessage(event);
    if (!isAssetLoadEvent(event) && !isRecoverableRuntimeFailure(message, (event as ErrorEvent).error?.stack)) {
      return;
    }
    event.preventDefault();
    void attemptRuntimeRecovery('asset-error');
  }, true);
}
